import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const SystemReports = () => {
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dateRange, setDateRange] = useState('7d');
  const [systemStats, setSystemStats] = useState({});
  const [performanceData, setPerformanceData] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [serviceStatus, setServiceStatus] = useState([]);

  useEffect(() => {
    fetchSystemReports();
  }, [dateRange]);

  const fetchSystemReports = async () => {
    setLoading(true);
    try {
      const [statsRes, performanceRes, errorsRes, activityRes, statusRes] = await Promise.all([
        fetch(`/api/system/reports/stats?range=${dateRange}`),
        fetch(`/api/system/reports/performance?range=${dateRange}`),
        fetch(`/api/system/reports/errors?range=${dateRange}`),
        fetch(`/api/system/reports/user-activity?range=${dateRange}`),
        fetch('/api/system/reports/service-status')
      ]);

      if (statsRes.ok) {
        const stats = await statsRes.json();
        setSystemStats(stats);
      }

      if (performanceRes.ok) {
        const performance = await performanceRes.json();
        setPerformanceData(performance);
      }

      if (errorsRes.ok) {
        const errors = await errorsRes.json();
        setErrorLogs(errors);
      }

      if (activityRes.ok) {
        const activity = await activityRes.json();
        setUserActivity(activity);
      }

      if (statusRes.ok) {
        const status = await statusRes.json();
        setServiceStatus(status);
      }
    } catch (error) {
      console.error('Error fetching system reports:', error);
      showSnackbar('Raporlar yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async (reportType) => {
    try {
      const response = await fetch(`/api/system/reports/export/${reportType}?range=${dateRange}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSnackbar('Rapor başarıyla indirildi', 'success');
      } else {
        showSnackbar('Rapor indirilirken hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      showSnackbar('Rapor indirilirken hata oluştu', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />;
      case 'warning': return <WarningIcon />;
      case 'error': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Sistem Raporları
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small">
            <InputLabel>Tarih Aralığı</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Tarih Aralığı"
            >
              <MenuItem value="1d">Son 1 Gün</MenuItem>
              <MenuItem value="7d">Son 7 Gün</MenuItem>
              <MenuItem value="30d">Son 30 Gün</MenuItem>
              <MenuItem value="90d">Son 90 Gün</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSystemReports}
            disabled={loading}
          >
            Yenile
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* System Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Toplam Kullanıcı
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.totalUsers || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemStats.userGrowth > 0 ? '+' : ''}{systemStats.userGrowth || 0}% geçen aya göre
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Aktif Şirket
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.activeCompanies || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemStats.companyGrowth > 0 ? '+' : ''}{systemStats.companyGrowth || 0}% geçen aya göre
                  </Typography>
                </Box>
                <BusinessIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Sistem Yükü
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.systemLoad || 0}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    CPU kullanımı
                  </Typography>
                </Box>
                <SpeedIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Depolama
                  </Typography>
                  <Typography variant="h4">
                    {systemStats.storageUsed || 0}GB
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {systemStats.storagePercentage || 0}% kullanımda
                  </Typography>
                </Box>
                <StorageIcon sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Chart */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Sistem Performansı"
              action={
                <IconButton onClick={() => handleExportReport('performance')}>
                  <DownloadIcon />
                </IconButton>
              }
            />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="cpu" stroke="#8884d8" name="CPU %" />
                  <Line type="monotone" dataKey="memory" stroke="#82ca9d" name="RAM %" />
                  <Line type="monotone" dataKey="disk" stroke="#ffc658" name="Disk %" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Servis Durumu" />
            <CardContent>
              <List>
                {serviceStatus.map((service) => (
                  <ListItem key={service.name}>
                    <ListItemIcon>
                      {getStatusIcon(service.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={service.name}
                      secondary={service.description}
                    />
                    <Chip
                      label={service.status}
                      color={getStatusColor(service.status)}
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Logs and User Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Hata Logları"
              action={
                <IconButton onClick={() => handleExportReport('errors')}>
                  <DownloadIcon />
                </IconButton>
              }
            />
            <CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tarih</TableCell>
                      <TableCell>Hata</TableCell>
                      <TableCell>Seviye</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errorLogs.slice(0, 10).map((error) => (
                      <TableRow key={error.id}>
                        <TableCell>
                          {new Date(error.timestamp).toLocaleString('tr-TR')}
                        </TableCell>
                        <TableCell>{error.message}</TableCell>
                        <TableCell>
                          <Chip
                            label={error.level}
                            color={error.level === 'ERROR' ? 'error' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Kullanıcı Aktivitesi"
              action={
                <IconButton onClick={() => handleExportReport('user-activity')}>
                  <DownloadIcon />
                </IconButton>
              }
            />
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={userActivity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="activeUsers" fill="#8884d8" name="Aktif Kullanıcı" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemReports;
